import apiClient from '../utils/apiClient';

/**
 * Dashboard API Service
 * Provides revenue, cashier, and staff monitoring data for Admin/Manager
 */
const dashboardAPI = {
  /**
   * Get Revenue Report
   * @param {Object} params - Query parameters
   * @param {string} params.branch_id - Branch ID (optional for ADMIN, auto-set for MANAGER)
   * @param {string} params.from_date - Start date (YYYY-MM-DD)
   * @param {string} params.to_date - End date (YYYY-MM-DD)
   * @param {'DAY'|'WEEK'|'MONTH'} [params.group_by] - Bucket cho daily_revenue (default 'DAY')
   * @returns {Promise} Revenue report data
   */
  getRevenueReport: async (params = {}) => {
    try {
      // apiClient.get() already returns res.data from interceptor
      return await apiClient.get({
        url: '/dashboard/revenue-report',
        params,
      });
    } catch (error) {
      console.error('Error fetching revenue report:', error);
      throw error;
    }
  },

  /**
   * Get Cashier Report
   * @param {Object} params - Query parameters
   * @param {string} params.branch_id - Branch ID (optional for ADMIN, auto-set for MANAGER)
   * @param {string} params.from_date - Start date (YYYY-MM-DD)
   * @param {string} params.to_date - End date (YYYY-MM-DD)
   * @returns {Promise} Cashier report data
   */
  getCashierReport: async (params = {}) => {
    try {
      // apiClient.get() already returns res.data from interceptor
      return await apiClient.get({
        url: '/dashboard/cashier-report',
        params,
      });
    } catch (error) {
      console.error('Error fetching cashier report:', error);
      throw error;
    }
  },

  /**
   * Monitor Employees
   * @param {Object} params - Query parameters
   * @param {string} params.branch_id - Branch ID (optional for ADMIN, auto-set for MANAGER)
   * @param {string} params.date_from - Start date (YYYY-MM-DD)
   * @param {string} params.date_to - End date (YYYY-MM-DD)
   * @param {string} params.date - End date (YYYY-MM-DD)
   * @param {string} params.status - Work status filter (WORKING|COMPLETED|ABSENT|UPCOMING)
   * @param {string} params.search - Employee name search (case-insensitive)
   * @param {number} params.page - Page number, starts at 1
   * @param {number} params.limit - Shifts per page (default 20, max 100)
   * @returns {Promise} Employee monitoring data — { summary, shifts, meta, timestamp }
   */
  monitorEmployees: async (params = {}) => {
    try {
      // apiClient.get() already returns res.data from interceptor
      return await apiClient.get({
        url: '/shift-tasks/monitor-employees',
        params,
      });
    } catch (error) {
      console.error('Error monitoring employees:', error);
      throw error;
    }
  },

  /**
   * #235631 — Danh sách phiếu chi cấu thành một con số chi phí trên Dashboard
   * (bấm vào "NVL: x đ" để xem đúng những phiếu cộng thành x).
   * @param {Object} params
   * @param {'MATERIAL'|'OTHER'|'EXCLUDED'} [params.group] - Nhóm chi phí, mặc định MATERIAL
   * @param {string} [params.branch_id]
   * @param {string} [params.from_date] - YYYY-MM-DD
   * @param {string} [params.to_date] - YYYY-MM-DD
   * @param {number} [params.page]
   * @param {number} [params.limit]
   */
  getExpenseVouchers: async (params = {}) => {
    return apiClient.get({ url: '/dashboard/expense-vouchers', params });
  },

  /**
   * #182201 — Đơn đặt hàng ĐANG CHỜ NHẬP KHO (đã đặt, chưa hoàn tất nhập kho).
   * Dùng cho dòng "N đơn hàng đang chờ nhập kho" trên Dashboard và bảng danh sách khi bấm vào.
   * Trả `counts.total` / `counts.not_arrived` (hàng chưa về) / `counts.awaiting_confirm`
   * (hàng đã về, NV chưa xác nhận nhập kho) — đếm trên toàn bộ đơn khớp bộ lọc.
   * @param {Object} params
   * @param {string} [params.branch_id]
   * @param {number} [params.page]
   * @param {number} [params.limit]
   */
  getPendingReceipts: async (params = {}) => {
    return apiClient.get({ url: '/dashboard/pending-receipts', params });
  },

  /**
   * #021749 — Lịch sử biến động Tiền Két (sổ quỹ), dùng cho modal mở từ ô "Tiền Két Hiện Tại".
   * Server đã GỘP sẵn các lần thu tiền mặt từ đơn hàng nằm liền nhau thành 1 dòng tổng doanh thu;
   * phiếu chi / chốt ca / sửa tay đứng riêng từng dòng. `meta.total` đếm theo DÒNG ĐÃ GỘP nên
   * phân trang khớp đúng với thứ bảng hiển thị.
   * @param {Object} params
   * @param {string} [params.branch_id]
   * @param {number} [params.page]
   * @param {number} [params.limit]
   */
  getCashBalanceHistory: async (params = {}) => {
    return apiClient.get({ url: '/dashboard/cash-balance-history', params });
  },

  /**
   * #131506 — CHI TIẾT các đơn cấu thành MỘT dòng "Tổng doanh thu tiền mặt (N đơn)".
   * Trả lại đúng câu cuối của góp ý gốc (#021749) đã bị cắt cụt lúc viết yêu cầu: bấm vào con số
   * doanh thu gộp là phải xem được từng đơn làm nên con số đó.
   * `group.amount` / `group.entry_count` do SERVER tính lại từ chính cụm đó — màn hình đối chiếu
   * với dòng gộp bằng hai số này chứ không tự cộng rồi tin số của mình.
   * @param {Object} params
   * @param {string} params.entry_id Phần sau `rev-` trong `key` của dòng gộp
   * @param {string} [params.branch_id]
   * @param {number} [params.page]
   * @param {number} [params.limit]
   */
  getCashRevenueGroupOrders: async (params = {}) => {
    return apiClient.get({
      url: '/dashboard/cash-balance-history/revenue-group',
      params,
    });
  },

  /**
   * #153102 — Tổng hợp Két theo KỲ đang chọn: đầu kỳ → doanh thu → chi → cuối kỳ.
   * Khác `getCashierReport().summary.current_cash_balance` (số dư SỐNG tại thời điểm hiện tại):
   * ở đây 4 số đều thuộc đúng khoảng `from_date`/`to_date` của bộ lọc chung trên Dashboard.
   * @param {Object} params
   * @param {string} [params.branch_id]
   * @param {string} [params.from_date] YYYY-MM-DD
   * @param {string} [params.to_date] YYYY-MM-DD
   */
  getCashPeriodSummary: async (params = {}) => {
    return apiClient.get({ url: '/dashboard/cash-period-summary', params });
  },

  /**
   * Get Fixed Costs (per-branch monthly fixed costs: rent, electricity, ...)
   * @param {Object} params
   * @param {string} [params.branch_id] - If omitted, returns all branches (ADMIN only).
   */
  getFixedCosts: async (params = {}) => {
    return apiClient.get({ url: '/dashboard/fixed-costs', params });
  },

  /**
   * Update Fixed Costs for a branch (ADMIN only)
   * @param {Object} body
   * @param {string} body.branch_id
   * @param {Array<{ id: string, name: string, monthly_amount: number, note?: string }>} body.items
   */
  updateFixedCosts: async (body) => {
    return apiClient.put({ url: '/dashboard/fixed-costs', data: body });
  },
};

export default dashboardAPI;
